import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback, useRef, type MouseEvent } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import {
  CheckCircle2,
  Lock,
  Info,
  Calendar as CalIcon,
  Sparkles,
  GraduationCap,
  LogOut,
  ShieldAlert,
  MessageCircle,
  AlertTriangle,
  Loader2,
  CreditCard,
  LayoutDashboard,
  UserCog,
  Receipt,
  Settings as SettingsIcon,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { isAdminEmail } from "@/lib/admin-access";
import { site } from "@/lib/site";
import { RefusalStrategyMatrix } from "@/components/student/RefusalStrategyMatrix";
import { VisaReadinessWidget } from "@/components/visa-readiness/VisaReadinessWidget";
import {
  createRazorpayOrder,
  verifyRazorpayPayment,
  markRazorpayPaymentFailed,
  syncMyRazorpayPaymentStatuses,
} from "@/lib/razorpay.functions";
import { getPremiumAccess } from "@/lib/premium-access.functions";
import { getMyMilestones, requestMyMilestone } from "@/lib/admin-students.functions";
import { clearPendingEnrollment, readPendingEnrollment, type PendingEnrollment } from "@/lib/pending-enrollment";
import { PLAN_META, type PlanKey as MetaPlanKey } from "@/lib/plans";
import { InterviewDateCard } from "@/components/student/InterviewDateCard";
import { OnboardingIntakeModal } from "@/components/student/OnboardingIntakeModal";
import { PreSessionGuidelines } from "@/components/student/PreSessionGuidelines";

import { logDateChange, getMyIntake, getMyPaymentProofLink } from "@/lib/student-lifecycle.functions";
import { RefusalUpgradeCard } from "@/components/student/RefusalUpgradeCard";
import { REFUSAL_UPGRADE_NOTE } from "@/lib/mock-scheduling.functions";
import { PlanBooking } from "@/components/student/PlanBooking";
import { getMyCustomAccess } from "@/lib/custom-access.functions";
import { isTesterEmail } from "@/lib/tester";
import { breakdownFromPayment, formatInr } from "@/lib/gst";
import { receiptUrlFor } from "@/lib/receipt-url";


declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// --- Config ---------------------------------------------------------------

const CONFIG = {
  plans: {
    basic_support: { name: "Basic Support", mocks: 2, ds_review: 0 },
    full_support: { name: "Full Support", mocks: 4, ds_review: 1 },
    refusal_support: { name: "Refusal Support", mocks: 5, ds_review: 1 },
  },
} as const;

const ADMIN_WA_DIGITS = site.phoneTel.replace(/[^\d]/g, "");
const AUTH_CHECK_TIMEOUT_MS = 6_000;
const PAYMENT_CHECK_TIMEOUT_MS = 8_000;
const PAYMENTS_LIMIT = 5;

const LOG_PREFIX = "[student-dashboard]";
const log = (m: string, x?: unknown) => console.info(`${LOG_PREFIX} ${m}`, x ?? "");

function openExternalWhatsApp(event: MouseEvent<HTMLAnchorElement>, href: string, afterOpen?: () => void) {
  event.preventDefault();
  if (typeof window === "undefined") return;

  const popup = window.open(href, "_blank", "noopener,noreferrer");
  if (popup) {
    popup.opener = null;
    popup.focus();
    afterOpen?.();
    return;
  }

  toast.info("Please allow popups for this site, then tap the WhatsApp button again.");
}

async function withTimeout<T>(p: PromiseLike<T>, ms: number, label: string): Promise<T | null> {
  let to: ReturnType<typeof setTimeout> | undefined;
  const t = new Promise<null>((r) => {
    to = setTimeout(() => {
      console.warn(`${LOG_PREFIX} ${label} timeout`);
      r(null);
    }, ms);
  });
  try {
    return await Promise.race([p, t]);
  } finally {
    if (to) clearTimeout(to);
  }
}

type PlanKey = keyof typeof CONFIG.plans;

function planKeyFromName(name: string | null | undefined): PlanKey | null {
  if (!name) return null;
  const k = name.toLowerCase();
  if (k.includes("refusal")) return "refusal_support";
  if (k.includes("full") || k.includes("fresher")) return "full_support";
  if (k.includes("basic")) return "basic_support";
  return null;
}

export const Route = createFileRoute("/student-dashboard")({
  head: () => ({
    meta: [{ title: "Student dashboard - WeLivdIt" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: StudentDashboard,
});

type PaymentRow = {
  plan_name: string;
  amount_paise: number;
  base_amount_paise?: number | null;
  gst_amount_paise?: number | null;

  status: "created" | "paid" | "failed";
  created_at: string;
  interview_date: string | null;
  buyer_name: string | null;
  buyer_phone: string | null;
  buyer_email: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id?: string | null;
  receipt_url?: string | null;
};


type DashTab = "dashboard" | "profile" | "calendar" | "billing" | "settings";

const DASH_TABS: ReadonlyArray<{ key: DashTab; label: string; icon: typeof LayoutDashboard; testingOnly?: boolean }> = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "profile", label: "Profile", icon: UserCog },
  { key: "calendar", label: "Calendar", icon: CalIcon, testingOnly: true },
  { key: "billing", label: "Plan Status & Billing", icon: Receipt },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

/** Experimental calendar view — testing/beta accounts only. */
function CalendarTab({ visaDate }: { visaDate: string | null }) {
  const selected = visaDate ? new Date(`${visaDate}T00:00:00`) : undefined;
  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-6">
      <h2 className="font-display text-xl font-semibold">Calendar</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {selected
          ? `Your visa interview is on ${selected.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.`
          : "Add your visa interview date in Profile to see it highlighted here."}
      </p>
      <div className="mt-4 inline-block rounded-xl border border-border p-2">
        <Calendar mode="single" selected={selected} defaultMonth={selected} />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Beta feature — visible to test accounts only.</p>
    </section>
  );
}

function BillingTab({
  planKey,
  payments,
  displayName,
  email,
}: {
  planKey: PlanKey | null;
  payments: PaymentRow[];
  displayName: string;
  email: string;
}) {
  const paid = payments.filter((p) => p.status === "paid");

  const downloadReceipt = (p: PaymentRow) => {
    const { base, gst, total } = breakdownFromPayment(p);
    const lines = [
      "WeLivdIt — Tax Invoice / Payment Receipt",
      "",
      `Name: ${p.buyer_name || displayName}`,
      `Email: ${p.buyer_email || email}`,
      `Plan: ${p.plan_name}`,
      `Base fee: ${formatInr(base)}`,
      `GST (18%): ${formatInr(gst)}`,
      `Total paid: ${formatInr(total)} (Base + 18% GST)`,
      `Status: ${p.status}`,
      `Payment Reference: ${p.razorpay_payment_id ?? p.razorpay_order_id ?? "—"}`,
      `Receipt link: ${receiptUrlFor(p) ?? "—"}`,
      `Date: ${format(new Date(p.created_at), "dd MMM yyyy, p")}`,
    ].join("\n");
    const blob = new Blob([lines], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `welivdit-receipt-${p.razorpay_order_id ?? p.created_at.slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="mt-6 space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Plan status</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Current plan:{" "}
          <strong className="text-foreground">{planKey ? CONFIG.plans[planKey].name : "No active plan"}</strong>
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Billing &amp; Tax Invoice</h2>
        {paid.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {paid.map((p) => {
              const { base, gst, total } = breakdownFromPayment(p);
              const url = receiptUrlFor(p);
              return (
                <li key={`${p.razorpay_order_id}-${p.created_at}`} className="rounded-xl border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{p.plan_name}</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(p.created_at), "dd MMM yyyy")}</p>
                  </div>

                  <div className="mt-4 text-center">
                    <div className="font-display text-3xl font-bold tracking-tight">{formatInr(total)}</div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatInr(base)} Base + {formatInr(gst)} (18% GST)
                    </p>
                  </div>

                  <dl className="mt-4 space-y-1.5 border-t border-border pt-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Base fee</dt>
                      <dd className="font-medium">{formatInr(base)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">GST (18%)</dt>
                      <dd className="font-medium">{formatInr(gst)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
                      <dt>Total paid</dt>
                      <dd>{formatInr(total)}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs text-muted-foreground">
                    All amounts in INR. 18% GST charged on top of the base fee.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {url && (
                      <Button asChild size="sm" className="gap-2">
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          📄 View / Download Tax Receipt
                        </a>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => downloadReceipt(p)}>
                      <Download className="h-4 w-4" /> Save summary
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}


function SettingsTab({ email }: { email: string }) {
  const KEY = "welivdit_student_prefs";
  const [prefs, setPrefs] = useState<{ emailUpdates: boolean; whatsappUpdates: boolean }>({
    emailUpdates: true,
    whatsappUpdates: true,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setPrefs({ ...prefs, ...(JSON.parse(raw) as object) });
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (k: "emailUpdates" | "whatsappUpdates") => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    toast.success("Preferences saved");
  };

  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-6">
      <h2 className="font-display text-xl font-semibold">Settings</h2>
      <p className="mt-1 text-sm text-muted-foreground">Signed in as {email}</p>
      <div className="mt-5 space-y-3">
        {(
          [
            ["emailUpdates", "Email updates about sessions and reminders"],
            ["whatsappUpdates", "WhatsApp updates from your Specialist"],
          ] as const
        ).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
            <span className="text-sm">{label}</span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={prefs[k]}
              onChange={() => toggle(k)}
              aria-label={label}
            />
          </label>
        ))}
      </div>
    </section>
  );
}

function StudentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState<string>("");
  const [displayName, setDisplayName] = useState("");
  const [consulate, setConsulate] = useState("Mumbai");
  const [paymentProofUrl, setPaymentProofUrl] = useState<string | null>(null);
  const [intakeVisaDate, setIntakeVisaDate] = useState<string | null>(null);

  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loadIssue, setLoadIssue] = useState("");
  const [authVerified, setAuthVerified] = useState(false);
  const [paying, setPaying] = useState(false);
  const [serverHasPaid, setServerHasPaid] = useState<boolean | null>(null);
  const [serverPaidPlan, setServerPaidPlan] = useState<string | null>(null);
  const [testerView, setTesterView] = useState<PlanKey | null>(null);
  const [customAccess, setCustomAccess] = useState(false);
  const [customPlan, setCustomPlan] = useState<PlanKey | null>(null);
  const [customAccessType, setCustomAccessType] = useState<"standard" | "testing" | null>(null);
  const [tab, setTab] = useState<DashTab>("dashboard");
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const ranRef = useRef(false);
  const resumedRef = useRef(false);


  const createOrderFn = useServerFn(createRazorpayOrder);
  const verifyPaymentFn = useServerFn(verifyRazorpayPayment);
  const markFailedFn = useServerFn(markRazorpayPaymentFailed);
  const syncPaymentStatusesFn = useServerFn(syncMyRazorpayPaymentStatuses);
  const getPremiumAccessFn = useServerFn(getPremiumAccess);
  const getMyIntakeFn = useServerFn(getMyIntake);
  const getProofLinkFn = useServerFn(getMyPaymentProofLink);
  const getCustomAccessFn = useServerFn(getMyCustomAccess);

  const reloadIntakeProof = useCallback(async () => {
    try {
      const res = await getMyIntakeFn();
      setIntakeVisaDate((res?.intake as { visa_date?: string | null } | null)?.visa_date ?? null);
      const fallback =
        res?.intake?.payment_proof_url ?? (res as unknown as { payment_proof_url?: string })?.payment_proof_url ?? null;
      let signed: string | null = null;
      try {
        signed = (await getProofLinkFn())?.url ?? null;
      } catch {
        /* fall back to stored url */
      }
      if (signed || fallback) setPaymentProofUrl(signed ?? fallback);
    } catch (err) {
      console.warn("Failed to reload intake proof URL:", err);
    }
  }, [getMyIntakeFn, getProofLinkFn]);

  const fetchPayments = useCallback(async () => {
    const paysResult = await withTimeout(
      supabase
        .from("payments")
        .select(
          "plan_name, amount_paise, status, created_at, interview_date, buyer_name, buyer_phone, buyer_email, razorpay_order_id, razorpay_payment_id, receipt_url, base_amount_paise, gst_amount_paise",
        )

        .in("status", ["paid", "failed", "created"])
        .order("created_at", { ascending: false })
        .limit(PAYMENTS_LIMIT),
      PAYMENT_CHECK_TIMEOUT_MS,
      "payments.select",
    );
    if (!paysResult) {
      setLoadIssue("Payment verification is taking longer than expected. Try refreshing in a minute.");
      return [] as PaymentRow[];
    }
    if (paysResult.error) {
      console.error(paysResult.error);
      setLoadIssue("We couldn't verify your payment right now. Please refresh or contact us.");
      return [] as PaymentRow[];
    }
    const rows = (paysResult.data ?? []) as PaymentRow[];
    setPayments(rows);
    return rows;
  }, []);

  const openRazorpay = useCallback(
    async (opts: {
      planKey: MetaPlanKey;
      name: string;
      phone: string;
      email: string | null;
      interviewDate: string | null;
      notes: string | null;
    }) => {
      setPaying(true);
      try {
        const ok = await loadRazorpayScript();
        if (!ok || !window.Razorpay) {
          toast.error("Couldn't load Razorpay. Check your connection and try again.");
          return;
        }
        const { data: sess } = await supabase.auth.getSession();
        if (!sess.session) {
          toast.error("Please sign in again to continue your payment.");
          navigate({ to: "/auth", search: { redirect: "/student-dashboard" }, replace: true });
          return;
        }
        const sessUser = sess.session.user;
        const authEmail = sessUser?.email ?? null;
        const payEmail = opts.email || authEmail || "";
        let payName =
          opts.name ||
          (sessUser?.user_metadata?.full_name as string | undefined) ||
          (sessUser?.user_metadata?.name as string | undefined) ||
          "";
        let payPhone = opts.phone || "";
        if ((!payName || !payPhone) && sessUser) {
          const { data: prof } = await supabase
            .from("profiles")
            .select("name, phone")
            .eq("user_id", sessUser.id)
            .maybeSingle();
          payName = payName || prof?.name || "";
          payPhone = payPhone || prof?.phone || "";
        }
        if (payPhone === "not-provided") payPhone = "";
        const order = await createOrderFn({
          data: {
            planKey: opts.planKey,
            name: payName || "WeLivdIt customer",
            phone: payPhone,
            email: payEmail || null,
            interviewDate: opts.interviewDate,
            notes: opts.notes,
          },
        });

        const rzp = new window.Razorpay({
          key: order.keyId,
          order_id: order.orderId,
          amount: order.amount,
          currency: order.currency,
          name: "WeLivdIt",
          description: order.planName,
          prefill: {
            name: payName,
            email: payEmail,
            contact: payPhone,
          },
          readonly: { email: Boolean(payEmail) },
          theme: { color: "#f97316" },
          handler: async (response: {
            razorpay_order_id: string;
            razorpay_payment_id: string;
            razorpay_signature: string;
          }) => {
            try {
              await verifyPaymentFn({ data: response });
              toast.success("Payment successful!");
              await fetchPayments();
              try {
                const access = await getPremiumAccessFn();
                setServerHasPaid(Boolean(access?.hasPaid));
                setServerPaidPlan(access?.planName ?? null);
              } catch (err) {
                console.error("Premium access recheck failed:", err);
              }
            } catch (err) {
              console.error(err);
              toast.error("Payment captured but verification failed. We'll reconcile manually.");
            } finally {
              setPaying(false);
            }
          },
          modal: {
            ondismiss: async () => {
              try {
                await markFailedFn({
                  data: { razorpay_order_id: order.orderId, reason: "User dismissed Razorpay modal" },
                });
              } catch (err) {
                console.error(err);
              }
              await fetchPayments();
              setPaying(false);
            },
          },
        });
        rzp.open();
      } catch (err) {
        console.error("Razorpay error:", err);
        toast.error(err instanceof Error ? err.message : "Couldn't start payment.");
        setPaying(false);
      }
    },
    [createOrderFn, verifyPaymentFn, markFailedFn, fetchPayments, navigate, getPremiumAccessFn],
  );

  const resumePendingEnrollment = useCallback(
    async (pending: PendingEnrollment) => {
      const extras: string[] = [];
      if (pending.email) extras.push(`Email: ${pending.email}`);
      if (pending.course) extras.push(`Course: ${pending.course}`);
      if (pending.visaCity) extras.push(`Visa interview city: ${pending.visaCity}`);
      if (pending.ofcInfo) extras.push(`OFC date & city: ${pending.ofcInfo}`);
      if (pending.notes) extras.push(`Notes: ${pending.notes}`);
      const combinedNotes = extras.join(" | ").slice(0, 500) || null;

      // Skip if the lead was already saved before the sign-in redirect.
      if (!pending.leadCaptured) {
        const { error: insertErr } = await supabase.from("enrollment_leads").insert({
          name: pending.name,
          phone: pending.phone,
          plan_key: pending.planKey,
          university: pending.university || null,
          interview_date: pending.interviewDate || null,
          notes: combinedNotes,
        });
        if (insertErr) console.error("Failed to record enrollment lead:", insertErr);
      }

      clearPendingEnrollment();
      toast.info("Resuming your payment…");
      await openRazorpay({
        planKey: pending.planKey,
        name: pending.name,
        phone: pending.phone,
        email: pending.email || null,
        interviewDate: pending.interviewDate || null,
        notes: pending.notes || null,
      });
    },
    [openRazorpay],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadIssue("");
    setAuthVerified(false);
    try {
      const userResult = await withTimeout(supabase.auth.getUser(), AUTH_CHECK_TIMEOUT_MS, "auth.getUser");
      if (!userResult) {
        setLoadIssue("Login check is taking too long. Please refresh or sign in again.");
        return;
      }
      const user = userResult.data.user;
      if (userResult.error || !user) {
        log("no-session redirect /auth");
        navigate({ to: "/auth", search: { redirect: "/student-dashboard" }, replace: true });
        return;
      }
      if (isAdminEmail(user.email)) {
        navigate({ to: "/admin", replace: true });
        return;
      }
      setAuthVerified(true);
      setEmail(user.email ?? "");
      setUserId(user.id);
      setDisplayName(
        (user.user_metadata?.full_name as string) ||
          (user.user_metadata?.name as string) ||
          user.email?.split("@")[0] ||
          "",
      );
      if (user.user_metadata?.consulate) {
        setConsulate(user.user_metadata.consulate as string);
      }

      await reloadIntakeProof();

      const { data: sessForSync } = await supabase.auth.getSession();
      if (sessForSync.session?.access_token) {
        await syncPaymentStatusesFn().catch((err) => console.error("Payment sync failed:", err));
      }
      await fetchPayments();

      try {
        const access = await getPremiumAccessFn();
        setServerHasPaid(Boolean(access?.hasPaid));
        setServerPaidPlan(access?.planName ?? null);
      } catch (err) {
        console.error("Premium access check failed:", err);
        setServerHasPaid(false);
        setServerPaidPlan(null);
      }

      try {
        const ca = await getCustomAccessFn();
        setCustomAccess(Boolean(ca?.hasAccess));
        setCustomPlan(planKeyFromName(ca?.planName ?? null));
        setCustomAccessType(ca?.accessType ?? null);
      } catch (err) {
        console.error("Custom access check failed:", err);
        setCustomAccess(false);
        setCustomAccessType(null);
      }

      if (!resumedRef.current) {
        const pending = readPendingEnrollment();
        if (pending) {
          resumedRef.current = true;
          void resumePendingEnrollment(pending);
        }
      }
    } catch (e) {
      console.error(e);
      setLoadIssue("Dashboard couldn't load completely. Please refresh or sign in again.");
    } finally {
      setLoading(false);
    }
  }, [
    navigate,
    fetchPayments,
    resumePendingEnrollment,
    syncPaymentStatusesFn,
    getPremiumAccessFn,
    reloadIntakeProof,
    getCustomAccessFn,
  ]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    load();
  }, [load]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  };

  const paidPayments = useMemo(() => payments.filter((p) => p.status === "paid"), [payments]);
  const activePlanKey: PlanKey | null = useMemo(() => {
    if (serverHasPaid !== true) return null;
    const serverKey = planKeyFromName(serverPaidPlan);
    if (serverKey) return serverKey;
    for (const p of paidPayments) {
      const k = planKeyFromName(p.plan_name);
      if (k) return k;
    }
    return null;
  }, [serverHasPaid, serverPaidPlan, paidPayments]);

  const failedPayment: PaymentRow | null = useMemo(() => {
    if (activePlanKey) return null;
    const failed = payments.find((p) => p.status === "failed");
    return failed ?? null;
  }, [payments, activePlanKey]);

  const handleTryAgain = useCallback(
    async (row: PaymentRow) => {
      const metaKey = (Object.keys(PLAN_META) as MetaPlanKey[]).find(
        (k) => PLAN_META[k].name.toLowerCase() === row.plan_name.toLowerCase(),
      );
      if (!metaKey) {
        toast.error("Couldn't find that plan. Please pick it again from Pricing.");
        navigate({ to: "/pricing" });
        return;
      }
      await openRazorpay({
        planKey: metaKey,
        name: row.buyer_name || displayName,
        phone: row.buyer_phone || "",
        email: row.buyer_email || email || null,
        interviewDate: row.interview_date,
        notes: "Retry after previous failed attempt",
      });
    },
    [openRazorpay, navigate, displayName, email],
  );

  // Refusal funnel: always available to enrolled Basic/Full students.
  const canUpgradeToRefusal =
    activePlanKey === "basic_support" || activePlanKey === "full_support";

  // Internal QA account can preview any plan's dashboard state on demand.
  const isTester = isTesterEmail(email);
  // Standard paid grants unlock the normal live dashboard; only testing/beta
  // accounts (and the internal QA account) get the experimental layout + Calendar.
  const hasGrantedAccess = customAccess || isTester;
  const isTestingAccess = isTester || customAccessType === "testing";
  const hasCustomDashboard = isTestingAccess;
  const effectivePlanKey: PlanKey | null =
    isTester && testerView ? testerView : (activePlanKey ?? (hasGrantedAccess ? customPlan : null));




  const handleRefusalUpgrade = useCallback(async () => {
    const latest = paidPayments[0];
    await openRazorpay({
      planKey: "refusal",
      name: latest?.buyer_name || displayName,
      phone: latest?.buyer_phone || "",
      email: latest?.buyer_email || email || null,
      interviewDate: null,
      notes: REFUSAL_UPGRADE_NOTE,
    });
  }, [openRazorpay, paidPayments, displayName, email]);

  if (loading) return <DashboardSkeleton />;

  if (!authVerified) {
    return (
      <section className="mx-auto max-w-5xl px-5 py-12">
        <h1 className="font-display text-4xl font-semibold">Student dashboard</h1>
        <div className="mt-6 rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              {loadIssue || "Please sign in again to view your dashboard."}
            </span>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={load}>
                Retry
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => navigate({ to: "/auth", search: { redirect: "/student-dashboard" } })}
              >
                Sign in again
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-5 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Your prep, your pace</p>
          <h1 className="mt-1 font-display text-4xl font-semibold">Hi {displayName}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{email}</p>
        </div>
        <Button variant="outline" onClick={handleSignOut} className="gap-2">
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>

      {loadIssue && (
        <div className="mt-6 rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              {loadIssue}
            </span>
            <Button type="button" variant="outline" size="sm" onClick={load}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {failedPayment && (
        <div className="mt-6 rounded-2xl border border-destructive/40 bg-destructive/5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="font-display text-lg font-semibold">Payment Failed for {failedPayment.plan_name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your previous attempt didn't go through. Tap Try Again to retry with the same plan details - no need
                  to fill the form again.
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={() => handleTryAgain(failedPayment)}
              disabled={paying}
              className="bg-gradient-cta text-primary-foreground gap-2"
            >
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Try Again
            </Button>
          </div>
        </div>
      )}

      {isTester && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#FAF8F5] p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tester view selector</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(Object.keys(CONFIG.plans) as PlanKey[]).map((k) => (
              <Button
                key={k}
                type="button"
                size="sm"
                variant={testerView === k ? "default" : "outline"}
                onClick={() => setTesterView(testerView === k ? null : k)}
              >
                View as {CONFIG.plans[k].name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {serverHasPaid === true && (
        <InterviewDateCard fallbackDate={intakeVisaDate} onSaved={(d) => setIntakeVisaDate(d)} />
      )}

      {hasCustomDashboard && (
        <div className="mt-6 flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-2">
          {DASH_TABS.filter((t) => !t.testingOnly || isTestingAccess).map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition",
                  tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                )}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>
      )}

      {hasCustomDashboard && tab !== "dashboard" ? (
        <>
          {tab === "profile" && (
            <section className="mt-6 rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold">Profile</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Update the onboarding details your Specialist uses to prep your sessions.
              </p>
              <Button className="mt-4 gap-2" onClick={() => setProfileEditOpen(true)}>
                <UserCog className="h-4 w-4" /> Edit onboarding details
              </Button>
              {profileEditOpen && (
                <OnboardingIntakeModal
                  defaultName={displayName || ""}
                  email={email}
                  userId={userId}
                  forceOpen
                  onClose={() => setProfileEditOpen(false)}
                  onDone={() => {
                    setProfileEditOpen(false);
                    void reloadIntakeProof();
                  }}
                />
              )}
            </section>
          )}

          {tab === "calendar" && isTestingAccess && <CalendarTab visaDate={intakeVisaDate} />}

          {tab === "billing" && (
            <BillingTab planKey={effectivePlanKey} payments={payments} displayName={displayName} email={email} />
          )}

          {tab === "settings" && <SettingsTab email={email} />}
        </>
      ) : !effectivePlanKey ? (
        <>
          <NoPlanGate email={email} hasFailedPayment={Boolean(failedPayment)} />
          {userId && (
            <div className="mt-8">
              <VisaReadinessWidget />
            </div>
          )}
        </>
      ) : (
        <>
          {/* PAID DASHBOARD */}
          <PaidDashboard
            planKey={effectivePlanKey}
            displayName={displayName}
            email={email}
            userId={userId}
            consulate={consulate}
            paidPayments={paidPayments}
            paymentProofUrl={isTester ? paymentProofUrl : null}
            onReloadProof={reloadIntakeProof}
            onRefusalUpgrade={handleRefusalUpgrade}
            paying={paying}
            hideMockWhatsApp={hasCustomDashboard}
          />

          {/* Plan-specific Calendly booking + mock status checks */}
          {hasCustomDashboard && <PlanBooking planKey={effectivePlanKey} />}

          {canUpgradeToRefusal && effectivePlanKey !== "refusal_support" && (
            <RefusalUpgradeCard
              paying={paying}
              onUpgrade={handleRefusalUpgrade}
              studentName={paidPayments[0]?.buyer_name || displayName}
              planName={CONFIG.plans[effectivePlanKey].name}
              purchaseDate={paidPayments[0]?.created_at ?? null}
            />
          )}


          {/* REFUSAL STRATEGY MATRIX (FOR REFUSAL PLAN ONLY) */}
          {userId && effectivePlanKey === "refusal_support" && (
            <div className="mt-8">
              <RefusalStrategyMatrix userId={userId} />
            </div>
          )}

          {/* ADMIN SUPPORT CARD */}
          <AdminSupportCard
            displayName={displayName}
            email={email}
            planKey={effectivePlanKey}
            paymentProofUrl={isTester ? paymentProofUrl : null}
          />

          {/* AI ASSESSMENT AT THE BOTTOM */}
          {userId && (
            <div className="mt-12">
              <VisaReadinessWidget />
            </div>
          )}
        </>
      )}


    </section>
  );
}

function AdminSupportCard({
  displayName,
  email,
  planKey,
  paymentProofUrl,
}: {
  displayName?: string;
  email?: string;
  planKey?: PlanKey;
  paymentProofUrl?: string | null;
}) {
  const planName = planKey ? CONFIG.plans[planKey].name : "Student Plan";
  const rawMsg =
    `Student: ${displayName || "A Student"} (${email || "N/A"})\n` +
    `Active Plan: ${planName} [VERIFIED PAID]\n` +
    (paymentProofUrl ? `Payment Proof: ${paymentProofUrl}\n` : "") +
    `\nHi WeLivdIt team! I have a question regarding my timeline and scheduling.`;

  const href = waLink(rawMsg);


  return (
    <div className="mt-8 rounded-3xl border border-border bg-gradient-to-br from-primary/5 to-card p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-start gap-3">
        <MessageCircle className="h-6 w-6 text-primary mt-0.5" />
        <div>
          <p className="font-display text-lg font-semibold">Need a hand?</p>
          <p className="text-sm text-muted-foreground mt-1">Have questions about your timeline or scheduling?</p>
        </div>
      </div>
      <a
        href={href}
        onClick={(event) => openExternalWhatsApp(event, href)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-medium shadow-soft hover:opacity-90 whitespace-nowrap"
      >
        <MessageCircle className="h-4 w-4" /> Contact WeLivdIt Admin
      </a>
    </div>
  );
}

function NoPlanGate({ email, hasFailedPayment }: { email: string; hasFailedPayment: boolean }) {
  return (
    <div className="mt-10 rounded-3xl border border-dashed border-border bg-card p-10 text-center">
      <ShieldAlert className="h-10 w-10 text-primary mx-auto" />
      <h2 className="mt-4 font-display text-2xl font-semibold">
        {hasFailedPayment ? "Retry payment to unlock your dashboard" : "Choose a plan to unlock your dashboard"}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        {hasFailedPayment
          ? "Your last Razorpay attempt failed. Use Try Again above and your DS-160 review and mock interview slots will unlock after payment succeeds."
          : "Pick a plan and pay securely with Razorpay. Your DS-160 review and mock interview slots unlock here as soon as your payment is verified."}
      </p>
      <Link
        to="/pricing"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-medium shadow-soft"
      >
        View plans & pay
      </Link>
      <p className="mt-4 text-xs text-muted-foreground">
        Already paid? Make sure you paid using the same email ({email}) or phone number on your profile, then refresh.
      </p>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <section className="mx-auto max-w-5xl px-5 py-12" aria-busy="true">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-3">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-3 w-48" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="mt-8 rounded-3xl border border-border bg-card p-7 space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-[200px] w-full rounded-2xl" />
      </div>
      <div className="mt-8 rounded-3xl border border-border bg-card p-7 space-y-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-[260px] w-full rounded-2xl" />
      </div>
    </section>
  );
}

// --- Paid view: manual WhatsApp booking ----------------------------------

const STORAGE_KEY = "welivdit:manual-bookings:v2";

type ManualState = {
  biometricDate: string;
  dsMessaged: boolean;
  interviewDate: string;
  mocksMessaged: number;
};

function readManual(): ManualState {
  if (typeof window === "undefined") {
    return { biometricDate: "", dsMessaged: false, interviewDate: "", mocksMessaged: 0 };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return { biometricDate: "", dsMessaged: false, interviewDate: "", mocksMessaged: 0, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { biometricDate: "", dsMessaged: false, interviewDate: "", mocksMessaged: 0 };
}

function writeManual(s: ManualState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function parseISODate(s: string): Date | undefined {
  if (!s) return undefined;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? undefined : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function DateField({
  id,
  value,
  onChange,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const date = parseISODate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            "mt-1 w-full justify-start text-left font-normal gap-2 rounded-xl",
            !date && "text-muted-foreground",
          )}
        >
          <CalIcon className="h-4 w-4 text-primary" />
          {date ? format(date, "EEE, d MMM yyyy") : <span>{placeholder ?? "Pick a date"}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => d && onChange(toISO(d))}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}

function formatNice(dateStr: string): string {
  if (!dateStr) return "";
  try {
    return format(new Date(dateStr + "T00:00:00"), "EEE, d MMM yyyy");
  } catch {
    return dateStr;
  }
}

function waLink(message: string) {
  return `https://wa.me/${ADMIN_WA_DIGITS}?text=${encodeURIComponent(message)}`;
}

function PaidDashboard({
  planKey,
  displayName,
  email,
  userId,
  consulate,
  paidPayments,
  paymentProofUrl,
  onReloadProof,
  onRefusalUpgrade,
  paying,
  hideMockWhatsApp,
}: {
  planKey: PlanKey;
  displayName: string;
  email: string;
  userId: string;
  consulate: string;
  paidPayments: PaymentRow[];
  paymentProofUrl?: string | null;
  onReloadProof?: () => void;
  onRefusalUpgrade?: () => void;
  paying?: boolean;
  hideMockWhatsApp?: boolean;
}) {
  const plan = CONFIG.plans[planKey];
  const [state, setState] = useState<ManualState>(() => readManual());
  const logDateChangeFn = useServerFn(logDateChange);
  const updateBio = (v: string) => {
    setState((s) => {
      if (s.biometricDate !== v) {
        logDateChangeFn({ data: { field: "biometrics", oldDate: s.biometricDate || null, newDate: v || null } }).catch(
          () => {},
        );
      }
      return { ...s, biometricDate: v };
    });
  };
  const updateInterview = (v: string) => {
    setState((s) => {
      if (s.interviewDate !== v) {
        logDateChangeFn({ data: { field: "interview", oldDate: s.interviewDate || null, newDate: v || null } }).catch(
          () => {},
        );
      }
      return { ...s, interviewDate: v };
    });
  };
  const [server, setServer] = useState<{
    mocks_done: number;
    mocks_requested: number;
    ds160_review_done: boolean;
    ds160_review_requested: boolean;
    ready_for_interview: boolean;
  } | null>(null);

  useEffect(() => writeManual(state), [state]);

  const getMyMilestonesFn = useServerFn(getMyMilestones);
  const requestMilestoneFn = useServerFn(requestMyMilestone);

  const refreshServer = useCallback(async () => {
    try {
      const res = await getMyMilestonesFn();
      if (res.found) {
        setServer({
          mocks_done: res.milestones.mocks_done,
          mocks_requested: res.milestones.mocks_requested,
          ds160_review_done: res.milestones.ds160_review_done,
          ds160_review_requested: res.milestones.ds160_review_requested,
          ready_for_interview: res.milestones.ready_for_interview,
        });
      }
    } catch (e) {
      console.warn("Couldn't load milestones", e);
    }
  }, [getMyMilestonesFn]);

  useEffect(() => {
    refreshServer();
    const onFocus = () => refreshServer();
    window.addEventListener("focus", onFocus);
    const id = window.setInterval(refreshServer, 45_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(id);
    };
  }, [refreshServer]);

  const dsRequested = server ? server.ds160_review_requested : state.dsMessaged;
  const dsDone = !!server?.ds160_review_done;
  const mocksRequested = server ? server.mocks_requested : state.mocksMessaged;
  const mocksBooked = Math.min(plan.mocks, mocksRequested);
  const allMocksRequested = mocksBooked >= plan.mocks;

  const formattedBioDate = formatNice(state.biometricDate) || "Not set yet";
  const formattedInterviewDate = formatNice(state.interviewDate) || "Not set yet";

  const latestPaid = paidPayments[0];
  const receiptLink = latestPaid ? (receiptUrlFor(latestPaid) ?? "Shared by admin on request") : "Shared by admin on request";
  const studentPhone = latestPaid?.buyer_phone || "Not provided";
  const studentLine = `Student: ${displayName || "A Student"} (${email || "N/A"} | ${studentPhone})`;

  // Always output proof link or system status line
  const proofLine = `\nPayment Proof Link: ${paymentProofUrl || "Verified in System"}`;

  // DS-160 Review WhatsApp message
  const ds160Message =
    `${studentLine}\n` +
    `Enrolled Plan: ${plan.name} [VERIFIED PAID]\n` +
    `Plan Receipt: ${receiptLink}` +
    `${proofLine}\n` +
    `Biometrics Date: ${formattedBioDate}\n` +
    `Target Consulate: ${consulate || "Mumbai"}\n\n` +
    `Hi WeLivdIt team! I'm ready to schedule my DS-160 Review session and align my prep timeline before my biometrics appointment.\n\n` +
    `Please share available slots to book my DS-160 review!`;

  // Bulk Mock Interviews WhatsApp message
  const mockMessage =
    `${studentLine}\n` +
    `Enrolled Plan: ${plan.name} [VERIFIED PAID]\n` +
    `Plan Receipt: ${receiptLink}` +
    `${proofLine}\n` +
    `Total Mocks Included: ${plan.mocks} Mocks\n` +
    `Visa Interview Date: ${formattedInterviewDate}\n` +
    `Target Consulate: ${consulate || "Mumbai"}\n\n` +
    `Hi WeLivdIt team! I've set my visa interview date and am ready to lock in and schedule all ${plan.mocks} mock interview sessions.\n\n` +
    `Please share the schedule so we can confirm all my mock slots!`;


  const flagDsRequest = async () => {
    setState((s) => ({ ...s, dsMessaged: true }));
    try {
      await requestMilestoneFn({ data: { kind: "ds160" } });
      await refreshServer();
    } catch (e) {
      console.warn("ds160 request failed", e);
    }
  };

  const flagMockRequest = async () => {
    setState((s) => ({ ...s, mocksMessaged: plan.mocks }));
    try {
      await requestMilestoneFn({ data: { kind: "mock", mockNumber: plan.mocks } });
      await refreshServer();
    } catch (e) {
      console.warn("mock request failed", e);
    }
  };

  return (
    <>
      <OnboardingIntakeModal
        defaultName={displayName || ""}
        email={email}
        userId={userId}
        onDone={() => {
          onReloadProof?.();
        }}
      />

      {/* 1. Plan Header Card */}
      <div className="mt-8 rounded-3xl border border-border bg-gradient-to-br from-primary/10 to-card p-6 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 text-primary" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display text-lg font-semibold">{plan.name}</p>
              <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge>
              {planKey !== "refusal_support" && (
                <button
                  type="button"
                  onClick={onRefusalUpgrade}
                  disabled={paying}
                  className="inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50"
                >
                  Faced a Refusal? Upgrade →
                </button>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {plan.mocks} mock interview{plan.mocks > 1 ? "s" : ""}
              {plan.ds_review ? " · DS-160 review included" : " · DS-160 review not included"}
            </p>
          </div>
        </div>
      </div>

      {/* 2. DYNAMIC PRE-SESSION GUIDELINES */}
      <div className="mt-8">
        <PreSessionGuidelines planKey={planKey} />
      </div>

      {/* 3. DS-160 Review */}
      <div className="mt-8 rounded-3xl border border-border bg-card p-7">
        <div className="flex items-center gap-3">
          <CalIcon className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-semibold">DS-160 Review</h2>
          {dsDone && <Badge className="bg-emerald-600 hover:bg-emerald-600">Approved ✓</Badge>}
          {!dsDone && dsRequested && (
            <Badge className="bg-amber-500 text-white hover:bg-amber-500">Pending approval</Badge>
          )}
        </div>

        {plan.ds_review === 0 ? (
          <div className="mt-5 rounded-2xl border border-dashed border-border p-6 bg-muted/40 opacity-80">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">DS-160 Review is locked on Basic Support</p>
                <p className="text-sm text-muted-foreground mt-1">
                  It's included in our Full and Refusal Support plans - a Specialist walks through your form line by
                  line before you submit.
                </p>
                <Link to="/pricing" className="mt-3 inline-block text-sm text-primary underline underline-offset-4">
                  Upgrade plan →
                </Link>
              </div>
            </div>
          </div>
        ) : dsDone ? (
          <div className="mt-5 rounded-2xl border border-emerald-300 bg-emerald-50 p-6 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-display text-lg font-semibold text-emerald-900">DS-160 Review complete</p>
              <p className="text-sm text-emerald-800/80 mt-1">Marked done by your Specialist.</p>
            </div>
          </div>
        ) : dsRequested ? (
          <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-6 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-display text-lg font-semibold text-amber-900">DS-160 Review requested</p>
              <p className="text-sm text-amber-800/80 mt-1">
                Biometric date: <strong>{formatNice(state.biometricDate) || "-"}</strong>. Admin will confirm your slot
                on WhatsApp.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setState((s) => ({ ...s, dsMessaged: false }))}
              >
                Change date
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-border p-6">
            <p className="font-display text-lg font-semibold">Add your OFC / Biometric date</p>
            <p className="text-sm text-muted-foreground mt-1">
              We schedule your DS-160 review about 2 weeks before your biometrics.
            </p>
            <div className="mt-4 max-w-sm">
              <Label htmlFor="biometricDate" className="text-sm">
                OFC / Biometric date
              </Label>
              <DateField
                id="biometricDate"
                value={state.biometricDate}
                onChange={updateBio}
                placeholder="Pick your biometric date"
              />
            </div>
            <a
              href={state.biometricDate ? waLink(ds160Message) : undefined}
              onClick={(e) => {
                if (!state.biometricDate) {
                  e.preventDefault();
                  return;
                }
                openExternalWhatsApp(e, waLink(ds160Message), () => {
                  void flagDsRequest();
                });
              }}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!state.biometricDate}
              className={
                "mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition " +
                (state.biometricDate
                  ? "bg-gradient-cta text-primary-foreground shadow-soft hover:opacity-90"
                  : "bg-muted text-muted-foreground cursor-not-allowed")
              }
            >
              <MessageCircle className="h-4 w-4" /> Message admin on WhatsApp
            </a>
          </div>
        )}
      </div>

      {/* 4. Mock Interviews */}
      <div className={cn("mt-8 rounded-3xl border border-border bg-card p-7", hideMockWhatsApp && "hidden")}>
        <div className="flex items-center gap-3">
          <CalIcon className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-semibold">Book & Schedule Your Mock Interviews</h2>
        </div>

        {allMocksRequested ? (
          <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="font-display text-lg font-semibold">
                Mock schedule requested! You're on track to ace your interview.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Visa interview date: <strong>{formatNice(state.interviewDate) || "Not set"}</strong>. Admin will lock in
                all your mock slots on WhatsApp.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setState((s) => ({ ...s, mocksMessaged: 0 }))}
              >
                Update Interview Date / Reschedule
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-border p-6">
            <p className="text-sm text-muted-foreground">
              Add your visa interview date below and message admin on WhatsApp. Admin will lock in and schedule all your
              mock sessions at once!
            </p>
            <div className="mt-4 max-w-sm">
              <Label htmlFor="interviewDate" className="text-sm">
                Visa interview date <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <DateField
                id="interviewDate"
                value={state.interviewDate}
                onChange={updateInterview}
                placeholder="Select your interview date (optional)"
              />
            </div>
            <a
              href={waLink(mockMessage)}
              onClick={(e) => {
                openExternalWhatsApp(e, waLink(mockMessage), () => {
                  void flagMockRequest();
                });
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition bg-gradient-cta text-primary-foreground shadow-soft hover:opacity-90"
            >
              <MessageCircle className="h-4 w-4" /> Message Admin to Schedule Mocks
            </a>
            <p className="mt-2 text-xs sm:text-sm italic text-muted-foreground/80">
              Tip: Sharing your visa interview date helps us prioritize your mock sessions before your interview day!
            </p>
          </div>
        )}
      </div>

    </>
  );
}
